
import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';
import { selectContentFromAppState } from '~src/spacesPlatform/selectors/ContentSelectors';

import { getAppStore } from '../../bootstrap/redux/store/AppStore';
import { WebClientListResourceFetcher } from '../../httpClient/fetchers/resourceFetchers/WebClientListResourceFetcher';
import { ResourceQuery } from '../../httpClient/resources/ResourceQuery';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { WebHttpClientWithErrorToastInstance } from '../../httpClient/WebHttpClientWithErrorToast';
import { IExplorableItemTypeInfo } from '../../spacesPlatform/models/resourceInfoParts';
import { BackgroundJobAllowedActions } from './BackgroundJobAllowedActions';
import { BackgroundJobStatus, BackgroundJobTaskType } from './BackgroundJobInfo';
import { CancelBackgroundJobRequest, ICancelBackgroundJobParams } from './CancelBackgroundJobRequest';
import { GetBackgroundJobsRequest, IBackgroundJobRaw, IGetBackgroundJobsResult } from './GetBackgroundJobsRequest';
import { AssociatedContentType, AssociatedContentTypes, IBackgroundJob } from './IBackgroundJob';

export interface IBackgroundJobInfo extends IBackgroundJob, IExplorableItemTypeInfo<'backgroundJob'> {
}

export namespace BackgroundJobsService {
  const mapAssociatedContentType = (contentType: string | null): AssociatedContentType | null => {
    if (contentType === null) {
      return null;
    }
    return (AssociatedContentTypes as readonly string[]).includes(contentType)
      ? (contentType as AssociatedContentType)
      : null;
  };

  export const backgroundJobsResponseMapper = (result: IGetBackgroundJobsResult): IBackgroundJobInfo[] =>
    result.backgroundJobs.map((j: IBackgroundJobRaw) => {
      return {
        ...j,
        contentType: 'backgroundJob',
        id: j.jobId + '',
        associatedContentName: j.contentName,
        associatedContentId: j.contentId,
        associatedContentType: mapAssociatedContentType(j.contentType),
      } as IBackgroundJobInfo;
  });
  export function all(): ResourceQuery<IBackgroundJobInfo> {
    const fetcher = new WebClientListResourceFetcher(GetBackgroundJobsRequest, backgroundJobsResponseMapper);
    return new ResourceQuery(fetcher);
  }

  function isCancellableSentToBridgeTask(backgroundJob: IBackgroundJobInfo): boolean {
    const isBridgeCancellationEnabled = FeatureFlags.bridgeExtractCancellationEnabled;
    const isBridgeTask = backgroundJob.taskType === BackgroundJobTaskType.Bridge;
    const isBridgeSpecificReportingEnabled = FeatureFlags.enableBridgeSpecificJobReportingEnabled;
    const status = backgroundJob.status;
    // If bridge-specific reporting is enabled, check for 'SentToBridge' status.
    // Otherwise, check for 'Completed' status (which represents a bridge job sent to bridge in the UI).
    // Handle SentToBridge specifically since the enum key ('SentToBridge') differs from value ('Sent to Bridge')
    const isSentToBridgeStatus = isBridgeSpecificReportingEnabled
      ? status === 'SentToBridge' || (status as string) === BackgroundJobStatus.SentToBridge
      : status === BackgroundJobStatus.Completed;
    const isSentToBridgeTask = isBridgeTask && isSentToBridgeStatus;
    return isBridgeCancellationEnabled && isSentToBridgeTask;
  }

  function isIncomplete(backgroundJob: IBackgroundJobInfo): boolean {
    const isPending = backgroundJob.status === BackgroundJobStatus.Pending;
    const isInProgress = backgroundJob.status === BackgroundJobStatus.InProgress;
    return isPending || isInProgress || isCancellableSentToBridgeTask(backgroundJob);
  }

  export function actions(id: string): Promise<BackgroundJobAllowedActions> {
    const items = selectContentFromAppState(getAppStore().getState());
    const backgroundJob: IBackgroundJobInfo | undefined = items.find((item: IBackgroundJobInfo) => item.jobId + '' === id) as IBackgroundJobInfo;
    if (!backgroundJob) {
      throw new Error('Failed to find background job with id ' + backgroundJob.jobId);
    }
    const incomplete = isIncomplete(backgroundJob);
    const isSystemJob = backgroundJob.taskType === BackgroundJobTaskType.System;
    const cancelBackgroundJob = incomplete && !isSystemJob;
    return Promise.resolve({ cancelBackgroundJob });
  }

  export function cancelBackgroundJob(cancelParms: ICancelBackgroundJobParams): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestAndDelay(new CancelBackgroundJobRequest(cancelParms));
  }
}
